import { useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Default Leaflet markers don't bundle correctly with webpack — use inline
// data-URI markers so we have zero external image dependencies.
const yellowIcon = L.divIcon({
    className: "tw-map-marker",
    html: `<div style="background:#FFCC00;border:2px solid #111827;border-radius:50%;width:24px;height:24px;display:flex;align-items:center;justify-content:center;font-weight:900;font-size:11px;color:#111827;box-shadow:0 2px 6px rgba(0,0,0,0.3)">•</div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
});

function FitBounds({ markers }) {
    const map = useMap();
    useEffect(() => {
        if (!markers || markers.length === 0) return;
        const bounds = L.latLngBounds(markers.map((m) => [m.lat, m.lng]));
        if (bounds.isValid()) {
            map.fitBounds(bounds, { padding: [40, 40], maxZoom: 11 });
        }
    }, [markers, map]);
    return null;
}

export default function MapView({ items }) {
    const containerRef = useRef(null);

    // Cluster identical PLZ-centroids by offsetting overlapping markers
    // slightly so users can click them individually.
    const markers = useMemo(() => {
        const buckets = new Map();
        return (items || []).map((it) => {
            const key = `${it.lat.toFixed(4)},${it.lng.toFixed(4)}`;
            const idx = buckets.get(key) || 0;
            buckets.set(key, idx + 1);
            // golden-angle offset (≈0.005°, ~500m) — keeps cards distinguishable
            const angle = (idx * 137.5 * Math.PI) / 180;
            const r = idx === 0 ? 0 : 0.012 * Math.sqrt(idx);
            return {
                ...it,
                lat: it.lat + r * Math.cos(angle),
                lng: it.lng + r * Math.sin(angle),
            };
        });
    }, [items]);

    if (!markers.length) {
        return (
            <div className="bg-white border border-gray-200 rounded-sm p-12 text-center text-gray-500" data-testid="map-empty">
                Keine Treffer mit gültiger PLZ — versuchen Sie andere Filter.
            </div>
        );
    }

    return (
        <div ref={containerRef} className="bg-white border border-gray-200 rounded-sm overflow-hidden" data-testid="map-view-wrap">
            <MapContainer
                center={[51.16, 10.45]}  // geographic center of Germany
                zoom={6}
                scrollWheelZoom
                style={{ height: 600, width: "100%" }}
            >
                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <FitBounds markers={markers} />
                {markers.map((m) => (
                    <Marker key={m.id} position={[m.lat, m.lng]} icon={yellowIcon}>
                        <Popup>
                            <div className="font-sans" style={{ minWidth: 200 }}>
                                {m.image && (
                                    <img src={m.image} alt="" style={{ width: "100%", aspectRatio: "4/3", objectFit: "cover", borderRadius: 4, marginBottom: 6 }} />
                                )}
                                <div style={{ fontWeight: 800, fontSize: 13, marginBottom: 2, color: "#111827" }}>
                                    {m.brand} {m.model}
                                </div>
                                <div style={{ fontSize: 11, color: "#6B7280", marginBottom: 4 }}>
                                    {m.year}{m.mileage ? ` · ${m.mileage.toLocaleString("de-DE")} km` : ""}{m.postal_code ? ` · ${m.postal_code}` : ""}
                                </div>
                                <div style={{ fontWeight: 900, fontSize: 16, color: "#111827", marginBottom: 6 }}>
                                    {Math.round(m.price).toLocaleString("de-DE")} €
                                </div>
                                <Link
                                    to={`/listing/${m.id}`}
                                    style={{ display: "inline-block", background: "#FFCC00", color: "#111827", fontWeight: 700, padding: "6px 12px", borderRadius: 3, fontSize: 11, textDecoration: "none", textTransform: "uppercase", letterSpacing: 0.5 }}
                                    data-testid={`map-popup-link-${m.id}`}
                                 target="_blank" rel="noopener noreferrer">
                                    Inserat öffnen →
                                </Link>
                            </div>
                        </Popup>
                    </Marker>
                ))}
            </MapContainer>
        </div>
    );
}
